import { useCallback, useEffect, useState } from 'react';

import { Button, Link, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import ImageUpload from 'components/ImageUpload';
import { useToast } from 'components/hooks/useToast';
import { uploadKeyResultsCSV } from 'screens/admin/Objectives/bulkUploadAccessControlApis';
import { StyledButtonContainer, StyledPanelHeader, StyledDivider } from 'screens/admin/Objectives/styles';
import { HeadingText, StyledDrawer } from 'styles/admin';
import {
  ColoredCircularProgress,
  NewStyledIconContainer,
  StyledButton,
  StyledTypography,
  VerticalFlexContainer,
  VerticallyAlignedFlexContainer,
} from 'styles/common';

import { sampleNewGoalFileURI } from 'constants/ProjectConstants';
import { StyledIcon } from 'constants/icons';

import { useBulkUploadPanelAccess } from './hooks/useBulkUploadPanelAccess';
import { useOKRBulkUploadPanel } from './hooks/useOKRBulkUploadPanel';

const sampleKRCsvUri = sampleNewGoalFileURI;

const GoalBulkUploadPanel = () => {
  const { isOpen, closeBulkUploadPanel: onCancel } = useOKRBulkUploadPanel({
    panelType: 'GOALS',
  });
  const toast = useToast();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const { bulkUploadKeyResultsIsAccessible } = useBulkUploadPanelAccess();

  const handleKeyDown = ({ keyCode }) => {
    if (keyCode === 27) onCancel();
  };

  const validateUploadFile = (file) => {
    if (file.size > 10 * 1000 * 1000) {
      const error = 'file size cannot be more than 10 mb.';
      toast({ title: error, status: 'error' });

      return false;
    }
    return true;
  };

  const selectUploadFile = (file) => {
    if (validateUploadFile(file)) setSelectedFile(file);
  };

  const uploadFile = async () => {
    const file = selectedFile;
    setUploading(true);

    const uploadKeyResultsCSVFn = uploadKeyResultsCSV;

    try {
      await uploadKeyResultsCSVFn(file);
      toast({
        title: 'File upload successful. Please refresh to see updated data.',
        status: 'success',
      });
      onCancel();
    } catch (error) {
      console.error('Error in uploading ', error?.message);
      toast({
        title: 'File upload failed. Please try again.',
        status: 'error',
      });
    } finally {
      setUploading(false);
    }
  };

  const resetState = useCallback(() => {
    setSelectedFile(null);
    setUploading(false);
  }, [setSelectedFile, setUploading]);

  useEffect(() => {
    // on close of side Panel, clear the state.
    if (!isOpen) {
      resetState();
    }
  }, [isOpen, resetState]);

  useEffect(() => {
    if (!bulkUploadKeyResultsIsAccessible && isOpen) {
      onCancel();
    }
  }, [bulkUploadKeyResultsIsAccessible, onCancel, isOpen]);

  return (
    <StyledDrawer anchor="right" open={isOpen} ModalProps={{ onBackdropClick: onCancel }} onKeyDown={handleKeyDown}>
      <StyledPanelHeader>
        <HeadingText>Bulk Upload Goals</HeadingText>
        <Button size="small" variant="text" onClick={() => onCancel()}>
          <StyledIcon color="textSecondary" size="header">
            <CloseIcon />
          </StyledIcon>
          <Typography style={{ textTransform: 'none' }} color="textSecondary">
            Cancel
          </Typography>
        </Button>
      </StyledPanelHeader>
      <StyledDivider />

      <VerticalFlexContainer>
        <div>
          <StyledTypography weight="bold" variant="body1">
            Import Goals from a CSV file.
          </StyledTypography>

          <VerticallyAlignedFlexContainer topGutter={3} bottomGutter={3} leftGutter={1} rightGutter={1}>
            <ImageUpload
              setFile={selectUploadFile}
              accept=".csv"
              icon={
                <StyledButton fontColor="primary" size="medium" variant="outlined" rightGutter={2}>
                  <StyledIcon>
                    <AddIcon />
                  </StyledIcon>
                  <StyledTypography weight="bold" variant="body1">
                    Choose File
                  </StyledTypography>
                </StyledButton>
              }
              disabled={uploading}
            />
            <StyledTypography color="textSecondary">
              {selectedFile === null ? 'No File Chosen' : selectedFile.name}
            </StyledTypography>
          </VerticallyAlignedFlexContainer>

          <StyledTypography variant="body1" color="textSecondary">
            <NewStyledIconContainer color="textSecondary" size="header">
              <InfoOutlinedIcon
                style={{
                  marginBottom: '-3px',
                  marginRight: '2px',
                }}
              />
            </NewStyledIconContainer>
            {`Please `}
            <Link download="MeshCSVSample.csv" href={sampleKRCsvUri} color="primary">
              download
            </Link>
            {` our sample file to refer to the upload format.`}
          </StyledTypography>
        </div>

        <div>
          <StyledButtonContainer>
            <StyledButton
              variant="contained"
              color="primary"
              size="large"
              onClick={uploadFile}
              disabled={selectedFile === null}
            >
              {uploading ? (
                <ColoredCircularProgress size="tiny" />
              ) : (
                <StyledTypography color="white">Upload File</StyledTypography>
              )}
            </StyledButton>
          </StyledButtonContainer>
        </div>
      </VerticalFlexContainer>
    </StyledDrawer>
  );
};

export { GoalBulkUploadPanel };
